/**
 * Admin → Knowledgebase sources (dwa_admin): choose which folders of the DWA shared
 * drive the Knowledgebase indexes. KB knowledgebase.md.
 *
 * The folder tree is read live from Google Drive (read-only) one level at a time;
 * ticking a folder includes all its subfolders (shown greyed as "included"). Save
 * replaces the selection (the backend checks every id in Drive) and the next sync
 * applies it: new folders are indexed, documents outside the selection removed.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faFolder,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { DWA } from '../theme/dwaTokens';
import { GradientPage } from '../hubs/HubCards';
import { HubLayout } from '../hubs/HubHeader';
import {
  browseKbSource,
  fetchKbSources,
  saveKbSourceFolders,
  syncKnowledgebase,
  updateKbSource,
  KbApiError,
  type KbSource,
} from '../services/kbApi';
import { diffSummary, isInherited, selectionDiff, toggleFolder, type ParentMap } from './kbSourcesTree';

export const KB_SOURCES_ROUTE = '/knowledgebase-sources';
export const KB_AUDIENCE_WARNING =
  'Everything in the selected folders becomes searchable by all DWA staff in Omega Wolf and Lupa.';

const ROOT = 'root';

const card: React.CSSProperties = {
  background: DWA.cardBg,
  borderRadius: DWA.radius,
  border: '1px solid rgba(255,255,255,0.3)',
  boxShadow: DWA.cardShadow,
  color: DWA.ink,
  padding: '1.1rem 1.15rem',
  minWidth: 0,
};

const muted: React.CSSProperties = { color: DWA.textSecondary, fontSize: '0.85rem', margin: 0, lineHeight: 1.5 };

const goldButton: React.CSSProperties = {
  padding: '0.55rem 1.1rem',
  backgroundColor: DWA.gold,
  color: DWA.onGold,
  border: 'none',
  borderRadius: DWA.radius,
  fontSize: '0.85rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: DWA.font,
  whiteSpace: 'nowrap',
};

const ghostButton: React.CSSProperties = {
  ...goldButton,
  background: 'transparent',
  color: DWA.ink,
  border: `1px solid ${DWA.cardBorder}`,
};

const sectionTitle: React.CSSProperties = {
  margin: '0 0 0.6rem',
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: DWA.textSecondary,
};

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

interface ChildState {
  ids: string[];
  next: string | null;
  loading: boolean;
  error: string | null;
}

/** The sources manager without the Hub shell (rendered directly in tests). */
export const KbSourcesManager: React.FC = () => {
  const [sources, setSources] = useState<KbSource[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const source = sources?.[0] ?? null;

  const [initial, setInitial] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [parents, setParents] = useState<ParentMap>({});
  const [names, setNames] = useState<Record<string, string>>({});
  const [children, setChildren] = useState<Record<string, ChildState>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string; invalid?: Record<string, string> | null } | null>(null);

  const applySource = useCallback((s: KbSource) => {
    setSources((cur) => (cur ? cur.map((x) => (x.id === s.id ? s : x)) : [s]));
    const ids = new Set(s.folders.map((f) => f.folder_id));
    setInitial(ids);
    setSelected(new Set(ids));
    setNames((cur) => {
      const next = { ...cur };
      for (const f of s.folders) next[f.folder_id] = f.path || f.name || f.folder_id;
      return next;
    });
  }, []);

  const loadSources = useCallback(() => {
    fetchKbSources()
      .then((r) => {
        setSources(r.sources);
        if (r.sources[0]) applySource(r.sources[0]);
        setLoadError(null);
      })
      .catch((e: Error) => setLoadError(e.message));
  }, [applySource]);

  useEffect(loadSources, [loadSources]);

  const loadChildren = useCallback(
    (parent: string, pageToken?: string | null) => {
      if (!source) return;
      setChildren((cur) => ({
        ...cur,
        [parent]: { ids: cur[parent]?.ids ?? [], next: cur[parent]?.next ?? null, loading: true, error: null },
      }));
      browseKbSource(source.id, parent, pageToken)
        .then((r) => {
          setParents((cur) => {
            const next = { ...cur };
            for (const f of r.folders) next[f.id] = parent === ROOT ? null : parent;
            return next;
          });
          setNames((cur) => {
            const next = { ...cur };
            const prefix = r.path.map((c) => c.name).join(' / ');
            for (const f of r.folders) next[f.id] = prefix ? `${prefix} / ${f.name}` : f.name;
            return next;
          });
          setChildren((cur) => ({
            ...cur,
            [parent]: {
              ids: [...(pageToken ? cur[parent]?.ids ?? [] : []), ...r.folders.map((f) => f.id)],
              next: r.next_page_token,
              loading: false,
              error: null,
            },
          }));
        })
        .catch((e: Error) =>
          setChildren((cur) => ({
            ...cur,
            [parent]: { ids: cur[parent]?.ids ?? [], next: cur[parent]?.next ?? null, loading: false, error: e.message },
          })),
        );
    },
    [source],
  );

  const sourceId = source?.id;
  useEffect(() => {
    if (sourceId !== undefined) loadChildren(ROOT);
    // Load the top level once per source; loadChildren changes with `source`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  const toggleExpand = (id: string) => {
    const opening = !expanded.has(id);
    setExpanded((cur) => {
      const next = new Set(cur);
      if (opening) next.add(id);
      else next.delete(id);
      return next;
    });
    if (opening && !children[id]) loadChildren(id);
  };

  const wholeDrive = Boolean(source?.index_whole_drive);
  const diff = useMemo(() => selectionDiff(initial, selected), [initial, selected]);
  const dirty = diff.added.length > 0 || diff.removed.length > 0;

  const save = () => {
    if (!source || !dirty) return;
    setSaving(true);
    setMessage(null);
    saveKbSourceFolders(source.id, [...selected])
      .then((r) => {
        applySource(r.source);
        setMessage({ kind: 'ok', text: `Saved: ${diffSummary(r)}. It applies on the next sync; click Sync now to apply it now.` });
      })
      .catch((e: Error) =>
        setMessage({ kind: 'error', text: `Not saved: ${e.message}`, invalid: e instanceof KbApiError ? e.invalid : null }),
      )
      .finally(() => setSaving(false));
  };

  const patch = (changes: Parameters<typeof updateKbSource>[1], done: string) => {
    if (!source) return;
    setMessage(null);
    updateKbSource(source.id, changes)
      .then((r) => {
        setSources((cur) => (cur ? cur.map((x) => (x.id === r.source.id ? r.source : x)) : [r.source]));
        setMessage({ kind: 'ok', text: done });
      })
      .catch((e: Error) => setMessage({ kind: 'error', text: `Not saved: ${e.message}` }));
  };

  const sync = () => {
    setMessage({ kind: 'ok', text: 'Starting sync…' });
    syncKnowledgebase()
      .then(() => {
        setMessage({ kind: 'ok', text: 'Sync queued. It reads the shared drive (read-only); refresh in a few minutes.' });
        setTimeout(loadSources, 3000);
      })
      .catch((e: Error) => setMessage({ kind: 'error', text: `Could not start a sync: ${e.message}` }));
  };

  const renderNode = (id: string, depth: number): React.ReactNode => {
    const inherited = isInherited(id, selected, parents, wholeDrive);
    const checked = inherited || selected.has(id);
    const open = expanded.has(id);
    const label = (names[id] ?? id).split(' / ').pop();
    const folderInfo = source?.folders.find((f) => f.folder_id === id);
    return (
      <li key={id} style={{ listStyle: 'none' }}>
        <div
          data-testid={`kb-node-${id}`}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: `0.25rem 0 0.25rem ${depth * 1.1}rem`, minWidth: 0 }}
        >
          <button
            type="button"
            onClick={() => toggleExpand(id)}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
            aria-expanded={open}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0.2rem', color: DWA.textSecondary, width: '1.5rem', flexShrink: 0 }}
          >
            <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
          </button>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', minWidth: 0, cursor: inherited ? 'default' : 'pointer', color: inherited ? DWA.textMuted : DWA.ink }}>
            <input
              type="checkbox"
              checked={checked}
              disabled={inherited}
              onChange={() => setSelected((cur) => toggleFolder(id, cur, parents, wholeDrive))}
              aria-label={label}
            />
            <FontAwesomeIcon icon={faFolder} style={{ color: inherited ? DWA.textMuted : DWA.gold, flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{label}</span>
          </label>
          {inherited && <span style={{ fontSize: '0.72rem', color: DWA.textMuted, whiteSpace: 'nowrap' }}>included</span>}
          {folderInfo?.document_count != null && selected.has(id) && (
            <span style={{ fontSize: '0.72rem', color: DWA.textSecondary, whiteSpace: 'nowrap', marginLeft: 'auto' }}>
              {folderInfo.document_count} docs
            </span>
          )}
        </div>
        {open && renderChildren(id, depth + 1)}
      </li>
    );
  };

  const renderChildren = (parent: string, depth: number): React.ReactNode => {
    const state = children[parent];
    return (
      <ul style={{ margin: 0, padding: 0 }}>
        {state?.ids.map((child) => renderNode(child, depth))}
        {state?.loading && <li style={{ ...muted, listStyle: 'none', paddingLeft: `${depth * 1.1 + 1.9}rem` }}>Loading…</li>}
        {state?.error && (
          <li role="alert" style={{ ...muted, listStyle: 'none', color: DWA.danger, paddingLeft: `${depth * 1.1 + 1.9}rem` }}>
            Couldn't load folders: {state.error}{' '}
            <button type="button" onClick={() => loadChildren(parent)} style={{ border: 'none', background: 'none', color: DWA.ink, textDecoration: 'underline', cursor: 'pointer' }}>
              Retry
            </button>
          </li>
        )}
        {state && !state.loading && !state.error && state.ids.length === 0 && (
          <li style={{ ...muted, listStyle: 'none', paddingLeft: `${depth * 1.1 + 1.9}rem`, fontSize: '0.78rem' }}>No subfolders</li>
        )}
        {state?.next && !state.loading && (
          <li style={{ listStyle: 'none', paddingLeft: `${depth * 1.1 + 1.9}rem` }}>
            <button type="button" onClick={() => loadChildren(parent, state.next)} style={{ ...ghostButton, padding: '0.25rem 0.7rem', fontSize: '0.78rem' }}>
              Load more folders
            </button>
          </li>
        )}
      </ul>
    );
  };

  const last = source?.last_success;
  const latest = source?.latest_run;

  return (
    <main style={{ maxWidth: 1000, margin: '0 auto', padding: '1.75rem clamp(1rem, 4vw, 2rem) 6rem', boxSizing: 'border-box' }}>
      <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', fontWeight: 700, margin: '0 0 0.35rem', color: '#ffffff' }}>Knowledgebase sources</h1>
      <p style={{ margin: 0, color: DWA.onDarkMuted, fontSize: '0.95rem' }}>
        Choose which shared-drive folders the <Link to="/hubs/knowledgebase" style={{ color: DWA.gold }}>Knowledgebase</Link> indexes.
        Omega Wolf only reads Drive; nothing here changes files or sharing in Google.
      </p>

      <div
        role="note"
        data-testid="kb-sources-warning"
        style={{ ...card, marginTop: '1.1rem', display: 'flex', gap: '0.6rem', alignItems: 'flex-start', background: '#fff7e6', border: '1px solid #f8d79a' }}
      >
        <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#b45309', marginTop: '0.2rem' }} />
        <p style={{ ...muted, color: '#7a4b00', fontWeight: 600 }}>{KB_AUDIENCE_WARNING}</p>
      </div>

      {loadError && (
        <div role="alert" style={{ ...card, marginTop: '1rem', color: DWA.danger, fontSize: '0.88rem' }}>
          Couldn't load the sources: {loadError}
        </div>
      )}
      {sources && !source && (
        <div style={{ ...card, marginTop: '1rem' }}>
          <p style={muted}>
            No source yet. Set <code>kb-drive-id</code> (and the service account key) in Key Vault; the first visit here or
            the first sync creates the source from it. See the Knowledge Bank entry <code>knowledgebase.md</code>.
          </p>
        </div>
      )}

      {source && (
        <>
          <section data-testid="kb-source-card" style={{ ...card, marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0, flex: '1 1 16rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{source.display_name}</h2>
                <p style={{ ...muted, fontSize: '0.8rem', overflowWrap: 'anywhere' }}>
                  Google shared drive · visible to all DWA staff · {source.enabled ? 'syncing' : 'paused'}
                </p>
                <p data-testid="kb-source-last-sync" style={{ ...muted, marginTop: '0.35rem' }}>
                  {last ? `Last synced ${formatWhen(last.finished_at ?? last.started_at)}` : 'Not synced yet'} ·{' '}
                  {source.document_count} document{source.document_count === 1 ? '' : 's'} indexed
                  {last?.counts?.listed !== undefined && ` · ${last.counts.listed} listed last sync`}
                  {latest?.status === 'running' && ' · sync running'}
                  {latest?.status === 'failed' && <span style={{ color: DWA.danger }}> · last sync failed</span>}
                </p>
              </div>
              <button type="button" onClick={sync} className="hub-gold-button" style={goldButton}>
                Sync now
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem', marginTop: '0.8rem' }}>
              <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={source.enabled}
                  onChange={(e) => patch({ enabled: e.target.checked }, e.target.checked ? 'Source enabled.' : 'Source paused: syncs skip it; its documents stay searchable.')}
                />
                Sync this source
              </label>
              <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={source.index_whole_drive}
                  onChange={(e) => {
                    if (e.target.checked && !window.confirm(`Index the whole shared drive? ${KB_AUDIENCE_WARNING}`)) return;
                    patch({ index_whole_drive: e.target.checked }, e.target.checked ? 'Whole drive on: the next sync indexes every folder.' : 'Whole drive off: only the selected folders are indexed.');
                  }}
                />
                Index the whole drive
              </label>
            </div>
          </section>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '1rem', alignItems: 'flex-start' }}>
            <section aria-label="Folders" style={{ ...card, flex: '2 1 22rem' }}>
              <h3 style={sectionTitle}>Folders in {source.display_name}</h3>
              {wholeDrive && <p style={{ ...muted, marginBottom: '0.5rem' }}>The whole drive is indexed, so every folder is included.</p>}
              <div style={{ overflowX: 'auto', maxHeight: '32rem', overflowY: 'auto' }}>{renderChildren(ROOT, 0)}</div>
            </section>

            <section aria-label="Selected folders" style={{ ...card, flex: '1 1 16rem' }}>
              <h3 style={sectionTitle}>Selected ({selected.size})</h3>
              {selected.size === 0 && !wholeDrive && (
                <p data-testid="kb-nothing-selected" style={{ ...muted, color: '#b45309' }}>
                  No folders selected: nothing will be indexed, and the next sync removes the {source.document_count} indexed document
                  {source.document_count === 1 ? '' : 's'}.
                </p>
              )}
              <ul style={{ margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {[...selected].map((id) => {
                  const info = source.folders.find((f) => f.folder_id === id);
                  return (
                    <li key={id} style={{ listStyle: 'none', display: 'flex', gap: '0.4rem', alignItems: 'flex-start', fontSize: '0.85rem' }}>
                      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                        {names[id] ?? id}
                        {!initial.has(id) && <strong style={{ color: DWA.positive }}> · new</strong>}
                        {info?.document_count != null && <span style={{ color: DWA.textMuted }}> · {info.document_count} docs</span>}
                        {info?.last_sync_error && <span style={{ color: DWA.danger }}> · last sync couldn't read it</span>}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${names[id] ?? id}`}
                        onClick={() => setSelected((cur) => { const n = new Set(cur); n.delete(id); return n; })}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: DWA.textSecondary }}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </li>
                  );
                })}
              </ul>
              {diff.removed.length > 0 && (
                <p style={{ ...muted, marginTop: '0.5rem', fontSize: '0.78rem' }}>
                  Removing: {diff.removed.map((id) => names[id] ?? id).join(', ')}
                </p>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.9rem' }}>
                <button type="button" onClick={save} disabled={!dirty || saving} className="hub-gold-button" style={{ ...goldButton, opacity: !dirty || saving ? 0.5 : 1 }}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                {dirty && (
                  <button type="button" onClick={() => setSelected(new Set(initial))} style={ghostButton}>
                    Undo
                  </button>
                )}
                <span data-testid="kb-diff-summary" style={{ ...muted, fontSize: '0.8rem' }}>{diffSummary(diff)}</span>
              </div>
            </section>
          </div>
        </>
      )}

      {message && (
        <div
          role={message.kind === 'error' ? 'alert' : 'status'}
          style={{ ...card, marginTop: '1rem', fontSize: '0.85rem', color: message.kind === 'error' ? DWA.danger : DWA.ink }}
        >
          {message.text}
          {message.invalid && (
            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
              {Object.entries(message.invalid).map(([id, why]) => (
                <li key={id}>
                  {names[id] ?? id}: {why}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </main>
  );
};

export const KbSourcesPage: React.FC = () => (
  <HubLayout>
    <GradientPage>
      <KbSourcesManager />
    </GradientPage>
  </HubLayout>
);

export default KbSourcesPage;
